/**
 * Public writer/reader card stored separately from `users/{uid}`.
 * Safe to read without authentication. Never include email, phone, tokens, or billing.
 */

import { get, ref, set } from "firebase/database";
import { db } from "../auth/firebase";

export function toPublicProfile(data = {}) {
  const role = String(data.role || "").toLowerCase();
  const roles = Array.isArray(data.roles) ? data.roles.map((r) => String(r).toLowerCase()) : [];
  const isWriter =
    data?.subscriptions?.writer === true || role === "writer" || roles.includes("writer");
  const displayName = String(data.displayName || data.fullName || data.authorName || "Writer")
    .trim()
    .slice(0, 80);
  return {
    displayName: displayName || "Writer",
    authorName: String(data.authorName || "").trim().slice(0, 80),
    bio: String(data.bio || "").trim().slice(0, 500),
    photoURL: String(data.photoURL || "").trim().slice(0, 800),
    isWriter,
    updatedAt: new Date().toISOString(),
  };
}

export async function syncPublicProfile(userId, data) {
  if (!userId || !data || typeof data !== "object") return;
  try {
    await set(ref(db, `publicProfiles/${userId}`), toPublicProfile(data));
  } catch (error) {
    console.warn("[publicProfile] sync failed:", error?.message || error);
  }
}

/**
 * Own profile reads `users/{uid}` (includes private fields). Everyone else reads `publicProfiles/{uid}`.
 * Returns null when the node is missing.
 */
export async function readProfileRecord(userId, { includePrivate = false } = {}) {
  if (!userId) return null;
  const path = includePrivate ? `users/${userId}` : `publicProfiles/${userId}`;
  const snapshot = await get(ref(db, path));
  if (!snapshot.exists()) return null;
  return snapshot.val();
}
